import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Share2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { USER_ROUTES } from "@/routes";
import type { Event } from "@/services/api/events";
import { EventCardHeader } from "./EventCardHeader";
import { EventStatsRow } from "./EventStatsRow";
import { EventContributionsSection } from "./EventContributionsSection";
import { getEventKindLabelKey, getEventMomentLabels, getEventTitle } from "@/utils/eventUtils";
import { calendarDaysUntil } from "@/lib/countdown";
import { useAuth } from "@/hooks/useAuth";
import { OnboardingResumePanel } from "@/components/customer/onboarding/OnboardingResumePanel";
import { useRouteLocale } from "@/hooks/useRouteLocale";

interface EventCardProps {
  event: Event;
  stats: {
    confirmedGuests: number;
    totalGifts: number;
    totalContributions: number;
    plan: 'free' | 'standard' | 'premium';
    bankLinked: boolean;
    bankLinkStatus: 'linked' | 'action_required' | 'pending' | 'not_linked';
  };
  currentLocale: string;
  onShare: (event: Event) => void;
  currentEventsCount: number;
  onDelete: (eventUuid: string) => void;
  /** JUB-157: hide the event from the dashboard (paid events' main action). */
  onArchive?: (eventUuid: string) => void;
}

export function EventCard({
  event,
  stats,
  currentLocale,
  onShare,
  currentEventsCount,
  onDelete,
  onArchive,
}: EventCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // JUB-176: calendar days, from the module the public countdown uses too.
  const daysUntil = calendarDaysUntil(event.date);
  const eventTitle = getEventTitle(event);

  // JUB-154: a draft whose wizard is still running resumes the wizard.
  const onboarding = event.onboarding?.status === 'started' ? event.onboarding : null;
  const wizardLocale = useRouteLocale();

  const handleCardClick = () => {
    navigate(
      onboarding
        ? USER_ROUTES.onboarding(wizardLocale, event.uuid, onboarding.step ?? undefined)
        : USER_ROUTES.event(currentLocale, event.uuid)
    );
  };

  const handleShareClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onShare(event);
  };

  return (
    <Card
      className="rounded-lg text-card-foreground shadow-sm cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all duration-300 border-2 border-pink-100 dark:border-pink-900/30 bg-white dark:bg-gray-900 overflow-hidden group"
      onClick={handleCardClick}
      data-testid="event-card"
    >
      <EventCardHeader
        title={eventTitle}
        date={event.date}
        eventType={t(getEventKindLabelKey(event))}
        subEvents={getEventMomentLabels(event, t)}
        eventUuid={event.uuid}
        currentLocale={currentLocale}
        isMenuOpen={isMenuOpen}
        onMenuToggle={() => setIsMenuOpen(!isMenuOpen)}
        onMenuClose={() => setIsMenuOpen(false)}
        currentEventsCount={currentEventsCount}
        onDelete={onDelete}
        onArchive={onArchive}
        isPaid={stats.plan !== 'free'}
        isLive={Boolean(event.live)}
      />
      <CardContent className="p-5 space-y-5">
        {onboarding ? (
          <OnboardingResumePanel eventUuid={event.uuid} onboarding={onboarding} />
        ) : (
        <>
        <EventStatsRow
          confirmedGuests={stats.confirmedGuests}
          totalGifts={stats.totalGifts}
          daysLeft={daysUntil}
          currentLocale={currentLocale}
        />
        <EventContributionsSection
          totalContributions={stats.totalContributions}
          currency={event.currency || 'RON'}
          plan={stats.plan}
          bankLinked={stats.bankLinked}
          bankLinkStatus={stats.bankLinkStatus}
          currentLocale={currentLocale}
          eventUuid={event.uuid}
        />
        <Button
          variant="outline"
          className="w-full border-2 border-dashed border-pink-300 dark:border-pink-700 bg-pink-50 dark:bg-pink-950/20 hover:bg-gradient-to-r hover:from-pink-50 hover:to-rose-50 dark:hover:from-pink-950/30 dark:hover:to-rose-950/20 hover:border-pink-400 dark:hover:border-pink-600 transition-all duration-200 text-sm"
          onClick={handleShareClick}
          data-testid="event-share-button"
        >
          <Share2 className="mr-2 h-4 w-4 text-pink-600 dark:text-pink-400" />
          <span className="text-xs">{t('events.share.title')}</span>
        </Button>
        </>
        )}
      </CardContent>
    </Card>
  );
}

